// Feedback module — Browse Feedback. Lists individual customer-feedback
// submissions, proxied from the standalone MF-Customer-Feedback-App-Api
// service via apparel-api's getFeedbackList resolver (see
// FeedbackService.ts). Same DataTable pattern as every other list screen in
// this app (e.g. customer.tsx), laid out to match the feedback app's own
// Admin Panel (Feedback tab): search bar, a Store/Channel/Date filter grid,
// and one wide table — customer/store info, all 8 rating dimensions,
// comments, and created date all as columns (no separate detail panel).
// DataTable's table wrapper already scrolls horizontally (`overflow-auto`),
// so this is naturally a scrollable table once it has this many columns —
// nothing extra needed for that.
//
// Field names here match the feedback service's SERVICE-TOKEN contract
// (server-to-server, static shared key — no user login in this path), which
// differs from its old JWT contract: rows carry storeLocationId (a raw
// UUID), not a kioskLocationName, so the Store column is resolved locally
// against the Locations list. There is no Supervisor filter or column —
// that contract has no submittedByUserId filter and no supervisor name on
// each row.
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { ColumnDef } from "@tanstack/react-table";
import { DateRange } from "react-day-picker";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import { getFeedbackListAction, getFeedbackLocationsAction } from "../../Store/actions/apparelAction";
import ApparelMenu from "../SubComponents/apparelMenu";
import { DataTable } from "../../shared/data-table";
import { DateRangePicker } from "../Reports/DateRangePicker";
import { Badge } from "ui/badge";
import { Button } from "ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "ui/select";
import { cn } from "lib/utils";
import {
  PURCHASE_CHANNELS,
  RATING_DIMENSIONS,
  channelLabel,
  channelBadgeClass,
  languageLabel,
  FeedbackKioskLocation,
  locationLabel,
} from "./feedbackConstants";

const PAGE_SIZE = 15;
const ALL = "all";

// Ratings are always whole stars 0-5 (the feedback app never sends
// fractions) — deliveryExperience is null (not 0) for non-online purchases,
// meaning "not applicable", shown as "—" rather than "0/5".
const StarRating = ({ value }: { value: any }) => {
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap tabular-nums">
      <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
      {Number(value)}/5
    </span>
  );
};

const FeedbackListScreen = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
  const { getFeedbackList, getFeedbackListLoad, getFeedbackLocations } = apparelReducerState;

  // Arriving from the Customers list ("Feedback Rating" column click) carries
  // the customer to filter by via navigation state — see customer.tsx.
  const [customerDetailId, setCustomerDetailId] = React.useState<string>(location.state?.customerDetailId || "");
  const [customerName, setCustomerName] = React.useState<string>(location.state?.customerName || "");
  const [page, setPage] = React.useState(1);
  const [search, setSearch] = React.useState("");
  const [storeLocationId, setStoreLocationId] = React.useState("");
  const [purchaseChannel, setPurchaseChannel] = React.useState("");
  const [rangeValue, setRangeValue] = React.useState<DateRange | undefined>(undefined);

  const fetchList = React.useCallback(
    (opts?: {
      page?: number;
      search?: string;
      storeLocationId?: string;
      purchaseChannel?: string;
      range?: DateRange;
      customerDetailId?: string;
    }) => {
      const p = opts?.page ?? page;
      const s = opts?.search ?? search;
      const store = opts?.storeLocationId ?? storeLocationId;
      const channel = opts?.purchaseChannel ?? purchaseChannel;
      const range = "range" in (opts || {}) ? opts?.range : rangeValue;
      const customer = "customerDetailId" in (opts || {}) ? opts?.customerDetailId : customerDetailId;
      dispatch(
        getFeedbackListAction({
          page: p,
          pageSize: PAGE_SIZE,
          search: s,
          storeLocationId: store,
          purchaseChannel: channel,
          from: range?.from ? moment(range.from).format("YYYY-MM-DD") : "",
          to: range?.to ? moment(range.to).format("YYYY-MM-DD") : "",
          customerDetailId: customer,
        })
      );
    },
    [dispatch, page, search, storeLocationId, purchaseChannel, rangeValue, customerDetailId]
  );

  React.useEffect(() => {
    fetchList({ page: 1, customerDetailId: location.state?.customerDetailId || "" });
    dispatch(getFeedbackLocationsAction());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearCustomerFilter = () => {
    setCustomerDetailId("");
    setCustomerName("");
    setPage(1);
    fetchList({ page: 1, customerDetailId: "" });
    navigate(location.pathname, { replace: true, state: null });
  };

  const onSearch = (query: string) => {
    setSearch(query);
    setPage(1);
    fetchList({ page: 1, search: query });
  };

  const onStoreChange = (value: string) => {
    const next = value === ALL ? "" : value;
    setStoreLocationId(next);
    setPage(1);
    fetchList({ page: 1, storeLocationId: next });
  };

  const onChannelChange = (value: string) => {
    const next = value === ALL ? "" : value;
    setPurchaseChannel(next);
    setPage(1);
    fetchList({ page: 1, purchaseChannel: next });
  };

  const onRangeChange = (range: DateRange | undefined) => {
    setRangeValue(range);
    if (!range || (range.from && range.to)) {
      setPage(1);
      fetchList({ page: 1, range });
    }
  };

  const onPageChange = (nextPage: number) => {
    setPage(nextPage);
    fetchList({ page: nextPage });
  };

  const listData = getFeedbackList?.getFeedbackList;
  const rows = listData?.items || [];
  const total = listData?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  const locations: FeedbackKioskLocation[] = (getFeedbackLocations?.getFeedbackLocations?.items || []).filter(
    (l: FeedbackKioskLocation) => l.isActive
  );
  // Rows only carry the raw storeLocationId — resolve a display name from
  // the Locations list rather than expecting the row itself to have one.
  const locationNameById = React.useMemo(() => {
    const map: Record<string, string> = {};
    for (const l of locations) map[l.id] = l.name;
    return map;
  }, [locations]);

  const columns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      { id: "customerName", header: "Customer Name", cell: ({ row }) => <span className="whitespace-nowrap">{row.original?.customerName || "—"}</span> },
      { id: "customerMobile", header: "Mobile", cell: ({ row }) => <span className="whitespace-nowrap">{row.original?.customerMobile || "—"}</span> },
      {
        id: "storeLocationId",
        header: "Store",
        cell: ({ row }) => (
          <span className="whitespace-nowrap">
            {locationNameById[row.original?.storeLocationId] || "—"}
          </span>
        ),
      },
      { id: "language", header: "Language", cell: ({ row }) => <span className="whitespace-nowrap">{languageLabel(row.original?.language)}</span> },
      {
        id: "purchaseChannel",
        header: "Channel",
        cell: ({ row }) => (
          <Badge variant="outline" className={cn("border-transparent whitespace-nowrap", channelBadgeClass(row.original?.purchaseChannel))}>
            {channelLabel(row.original?.purchaseChannel)}
          </Badge>
        ),
      },
      // One column per rating dimension — mirrors the feedback app's own
      // wide table exactly, rather than tucking these into a side panel.
      ...RATING_DIMENSIONS.map((d) => ({
        id: d.key,
        header: d.fullLabel,
        cell: ({ row }: any) => <StarRating value={row.original?.ratings?.[d.key]} />,
      })),
      {
        id: "comments",
        header: "Comments",
        cell: ({ row }) => (
          <span className="block max-w-[220px] truncate" title={row.original?.comments || undefined}>
            {row.original?.comments || "—"}
          </span>
        ),
      },
      {
        id: "createdAt",
        header: "Created At",
        cell: ({ row }) => <span className="whitespace-nowrap">{row.original?.createdAt ? moment(row.original.createdAt).format("DD MMM YYYY, hh:mm A") : "—"}</span>,
      },
    ],
    [locationNameById]
  );

  return (
    <div className="mz h-full overflow-y-auto px-6 py-4">
      <ApparelMenu selectedKey="feedback-list" />

      {customerDetailId && (
        <div className="mb-3 flex items-center gap-2 text-sm">
          <Badge variant="outline" className="gap-1.5">
            Filtered by customer{customerName ? `: ${customerName}` : ""}
            <button type="button" onClick={clearCustomerFilter} className="ml-1 text-muted-foreground hover:text-foreground">
              ×
            </button>
          </Badge>
        </div>
      )}

      <DataTable
        columns={columns}
        data={rows}
        loading={getFeedbackListLoad}
        searchable
        searchPlaceholder="Search name, mobile, or comments"
        onSearch={onSearch}
        toolbar={
          <div className="grid w-full grid-cols-2 gap-2 sm:w-auto sm:grid-cols-3">
            <Select value={storeLocationId || ALL} onValueChange={onStoreChange}>
              <SelectTrigger className="w-full sm:w-[160px]">
                <SelectValue placeholder="All Stores" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All Stores</SelectItem>
                {locations.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{locationLabel(s)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={purchaseChannel || ALL} onValueChange={onChannelChange}>
              <SelectTrigger className="w-full sm:w-[160px]">
                <SelectValue placeholder="All Channels" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All Channels</SelectItem>
                {PURCHASE_CHANNELS.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <DateRangePicker value={rangeValue} onChange={onRangeChange} disableFuture placeholder="Date Range" />
          </div>
        }
        hideDensityToggle
      />

      {/* Pagination footer — matches the feedback app's Admin Panel ("Showing
          X-Y of Z · Page A/B" + compact prev/next). */}
      <div className="mt-3 flex items-center justify-between text-sm text-muted-foreground">
        <span>
          Showing {rangeStart}-{rangeEnd} of {total} · Page {page}/{totalPages}
        </span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackListScreen;
